import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  BodyText,
  Button,
  CheckboxTile,
  Flex,
  Heading,
  SectionHeader,
} from "@utilitywarehouse/hearth-react";

import NavigationButtons from "../../components/Questions/NavigationButtons";
import { PATHS } from "../../config/routeConfig";
import QuestionsAnswersLayout from "../../components/QuestionsAnswersLayout";
import { MappedAnswers } from "../../context/vulnerability/VulnerabilityContext";
import { mapPSRAnswersSummary } from "../utils";
import { useVulnerabilityContext } from "../../context/vulnerability/useVulnerabilityContext";
import { checkTokenExpiryOnInterval } from "../../context/auth/Token";
import { useAuthContext } from "../../context/auth/useAuthContext";
import Unauthenticated from "../../components/Unauthenticated";
import { useSubmitAssessmentCustomer } from "../../graphql/customer/useSubmitAssessmentCustomer";
import { CustomerPSRSubmissionDataTest } from "../CustomerPSRSubmission/constants";
import Loader from "../../components/Loader";

import { CustomerPSRSummaryDataTest } from "./constants";

const CustomerPSRSummary = () => {
  const navigate = useNavigate();

  const { answers, setSubmitted } = useVulnerabilityContext();
  const [checkedTC, setCheckedTC] = useState(false);
  const { authenticated, setAuthenticated } = useAuthContext();
  const {
    submitAssessment,
    loading: submitLoading,
    error: submitError,
  } = useSubmitAssessmentCustomer();

  const mappedAnswers = useMemo((): MappedAnswers => {
    return mapPSRAnswersSummary(answers);
  }, [answers]);

  const onChangeCheckedTC = (checked: boolean) => {
    setCheckedTC(checked);
  };

  const handleSubmission = async () => {
    const result = await submitAssessment();
    if (result && !result.errors) {
      setSubmitted(true);
      navigate(`/${PATHS.Submission}`);
    }
  };

  useEffect(() => {
    checkTokenExpiryOnInterval(authenticated, setAuthenticated);
  }, []);

  if (submitLoading) {
    return <Loader />;
  }

  return (
    <Flex direction="column" marginTop="150">
      {!authenticated ? (
        <Unauthenticated />
      ) : (
        <Flex direction="column" gap="500">
          {submitError ? (
            <Flex
              direction="column"
              spacing="lg"
              data-testid={CustomerPSRSubmissionDataTest.ERROR_CONTAINER}
            >
              <BodyText>Something went wrong</BodyText>
              <BodyText color="secondary">
                Something went wrong when submitting your answers. Please click
                below to try again, or come back later to try again.
              </BodyText>

              <Button
                onClick={submitAssessment}
                variant="solid"
                color="secondary"
                style={{ marginTop: "16px", maxWidth: "fit-content" }}
              >
                Try Again
              </Button>
            </Flex>
          ) : (
            <Flex
              direction="column"
              gap="500"
              spacing="2xl"
              data-testid={CustomerPSRSummaryDataTest.SUMMARY_CONTAINER}
            >
              <Flex direction="column" gap="350">
                <Heading size="lg">
                  Please check the answers you’ve provided are correct.
                </Heading>
                <BodyText>
                  We really appreciate you taking the time to fill out this
                  survey and we hope that we're able to make your life that
                  little bit easier. Please read through your answers carefully
                  before submitting.
                </BodyText>
              </Flex>

              <Flex direction="column" gap="25">
                <SectionHeader heading="Answer Summary" />
                <QuestionsAnswersLayout mappedAnswers={mappedAnswers} />
              </Flex>

              <CheckboxTile
                data-testid={CustomerPSRSummaryDataTest.SUMMARY_TC_CHECKBOX}
                checked={checkedTC}
                onCheckedChange={onChangeCheckedTC}
                label={
                  <BodyText>
                    The sensitive information you’ve supplied in this form will
                    be stored securely on your account with us. To enable us to
                    offer you the additional priority services you’ve requested,
                    and meet your requirements, we may need to pass on some of
                    this information to the distribution network operators in
                    your area. To give consent for us to store, share and
                    process the sensitive information you’ve provided please
                    tick the consent box above.
                  </BodyText>
                }
              />
            </Flex>
          )}

          <NavigationButtons
            onBack={() => navigate(`/${PATHS.PSRServices}`)}
            onContinue={handleSubmission}
            forwardName="Submit"
            disableNext={!checkedTC}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default CustomerPSRSummary;
